/**
 * Minimap.ts
 * 2D top-down canvas minimap for the octile dungeon grid.
 * Renders in the right sidebar panel, shows cell types with color
 * coding, selection highlights, room grouping, and click-to-select.
 */

import type { GridCell, OctileCell, SquareConnector, GridPos, CellType, CellTheme } from '../types/OctileTypes.ts'
import { PASSABLE_WALLS } from '../types/OctileTypes.ts'
import { gridKey } from '../grid/GridAddressing.ts'
import { CELL_TYPE_VISUALS, THEME_VISUALS } from '../grid/CellVisuals.ts'

const CONNECTOR_COLOR = '#47403a'
const PASSAGE_COLOR = '#3a3530'
const SELECTED_COLOR = '#00ff88'
const ROOM_BORDER_COLOR = 'rgba(0, 255, 136, 0.35)'
const BG_COLOR = '#1a1a1e'

// ─── Types ───────────────────────────────────────────────────

export interface MinimapCallbacks {
    onCellClicked?: (row: number, col: number) => void
}

// ─── Minimap ─────────────────────────────────────────────────

export class Minimap {
    private canvas: HTMLCanvasElement
    private ctx: CanvasRenderingContext2D
    private grid: GridCell[][] | null = null
    private selectedCells = new Set<string>()
    private callbacks: MinimapCallbacks

    // Computed layout (recalculated on each render)
    private cellSize = 0
    private offsetX = 0
    private offsetY = 0
    private minR = 0
    private minC = 0
    private maxR = 0
    private maxC = 0

    constructor(canvas: HTMLCanvasElement, callbacks: MinimapCallbacks = {}) {
        this.canvas = canvas
        this.ctx = canvas.getContext('2d')!
        this.callbacks = callbacks

        canvas.addEventListener('click', (e) => this.handleClick(e))
        canvas.style.cursor = 'pointer'

        const observer = new ResizeObserver(() => this.render())
        observer.observe(canvas.parentElement || canvas)
    }

    setGrid(grid: GridCell[][]): void {
        this.grid = grid
        this.render()
    }

    setSelection(cells: GridPos[]): void {
        this.selectedCells.clear()
        for (const c of cells) {
            this.selectedCells.add(gridKey(c.row, c.col))
        }
        this.render()
    }

    clearSelection(): void {
        this.selectedCells.clear()
        this.render()
    }

    render(): void {
        if (!this.grid) return

        const container = this.canvas.parentElement
        if (!container) return

        const rect = container.getBoundingClientRect()
        if (rect.width < 10) return

        const dpr = window.devicePixelRatio || 1
        const w = Math.floor(rect.width)
        const h = Math.floor(rect.width) // square aspect ratio

        this.canvas.width = w * dpr
        this.canvas.height = h * dpr
        this.canvas.style.width = `${w}px`
        this.canvas.style.height = `${h}px`

        const ctx = this.ctx
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.clearRect(0, 0, w, h)
        ctx.fillStyle = BG_COLOR
        ctx.fillRect(0, 0, w, h)

        // Find active cell bounds
        this.computeBounds()
        if (this.minR > this.maxR) return // no active cells

        const spanR = this.maxR - this.minR + 1
        const spanC = this.maxC - this.minC + 1

        const padding = 10
        const availW = w - padding * 2
        const availH = h - padding * 2

        this.cellSize = Math.min(availW / spanC, availH / spanR)
        // Offsets position the top-left of the drawing area (flipped axes applied in cellCenter)
        this.offsetX = padding + (availW - this.cellSize * spanC) / 2
        this.offsetY = padding + (availH - this.cellSize * spanR) / 2

        // Layer 1: Draw passages between adjacent cells
        this.drawPassages()

        // Layer 2: Draw cells
        this.drawCells()

        // Layer 3: Selection highlights
        this.drawSelectionHighlights()
    }

    // ─── Drawing Layers ──────────────────────────────────

    private drawPassages(): void {
        const grid = this.grid!
        const cs = this.cellSize
        const ctx = this.ctx

        ctx.fillStyle = PASSAGE_COLOR

        for (let r = 0; r < grid.length; r++) {
            for (let c = 0; c < (grid[0]?.length ?? 0); c++) {
                const cell = grid[r]?.[c]
                if (!cell?.active || cell.kind !== 'octagon') continue

                const oct = cell as OctileCell
                const { cx, cy } = this.cellCenter(r, c)

                // E neighbor (octagon at r, c+2): wall index 2
                if (PASSABLE_WALLS.includes(oct.walls[2])) {
                    const nr = r, nc = c + 2
                    const neighbor = grid[nr]?.[nc]
                    if (neighbor?.active) {
                        const n = this.cellCenter(nr, nc)
                        const pw = cs * 0.25  // passage width
                        const left = Math.min(cx, n.cx)
                        ctx.fillRect(left, cy - pw / 2, Math.abs(n.cx - cx), pw)
                    }
                }

                // S neighbor (octagon at r+2, c): wall index 4
                if (PASSABLE_WALLS.includes(oct.walls[4])) {
                    const nr = r + 2, nc = c
                    const neighbor = grid[nr]?.[nc]
                    if (neighbor?.active) {
                        const n = this.cellCenter(nr, nc)
                        const pw = cs * 0.25
                        const top = Math.min(cy, n.cy)
                        ctx.fillRect(cx - pw / 2, top, pw, Math.abs(n.cy - cy))
                    }
                }

                // SE connector (r+1, c+1): wall index 3
                if (PASSABLE_WALLS.includes(oct.walls[3])) {
                    const nr = r + 1, nc = c + 1
                    const neighbor = grid[nr]?.[nc]
                    if (neighbor?.active) {
                        const n = this.cellCenter(nr, nc)
                        const pw = cs * 0.18
                        ctx.save()
                        ctx.translate((cx + n.cx) / 2, (cy + n.cy) / 2)
                        ctx.rotate(Math.PI / 4)
                        ctx.fillRect(-pw, -pw / 2, pw * 2, pw)
                        ctx.restore()
                    }
                }

                // SW connector (r+1, c-1): wall index 5
                if (PASSABLE_WALLS.includes(oct.walls[5])) {
                    const nr = r + 1, nc = c - 1
                    const neighbor = grid[nr]?.[nc]
                    if (neighbor?.active) {
                        const n = this.cellCenter(nr, nc)
                        const pw = cs * 0.18
                        ctx.save()
                        ctx.translate((cx + n.cx) / 2, (cy + n.cy) / 2)
                        ctx.rotate(-Math.PI / 4)
                        ctx.fillRect(-pw, -pw / 2, pw * 2, pw)
                        ctx.restore()
                    }
                }
            }
        }
    }

    private drawCells(): void {
        const grid = this.grid!
        const ctx = this.ctx

        for (let r = 0; r < grid.length; r++) {
            for (let c = 0; c < (grid[0]?.length ?? 0); c++) {
                const cell = grid[r]?.[c]
                if (!cell?.active) continue

                const { cx, cy } = this.cellCenter(r, c)

                if (cell.kind === 'octagon') {
                    this.drawOctagonCell(cell, cx, cy)
                } else if (cell.kind === 'connector') {
                    this.drawConnectorCell(cell, cx, cy)
                }
            }
        }
    }

    private drawOctagonCell(cell: OctileCell, cx: number, cy: number): void {
        const ctx = this.ctx
        const radius = this.cellSize * 0.42

        // Fill with cell type color (fallback to theme color)
        ctx.fillStyle = CELL_TYPE_VISUALS[cell.type]?.hex || THEME_VISUALS[cell.theme]?.hex || '#4a4a4a'
        ctx.beginPath()
        this.octagonPath(cx, cy, radius)
        ctx.fill()

        // Room membership: green tinted border
        if (cell.partOfRoom) {
            ctx.strokeStyle = ROOM_BORDER_COLOR
            ctx.lineWidth = 1.5
            ctx.stroke()
        }
    }

    private drawConnectorCell(cell: SquareConnector, cx: number, cy: number): void {
        const ctx = this.ctx
        const radius = this.cellSize * 0.28

        ctx.fillStyle = CONNECTOR_COLOR
        ctx.beginPath()
        this.diamondPath(cx, cy, radius)
        ctx.fill()

        if (cell.isPartOfRoom) {
            ctx.strokeStyle = ROOM_BORDER_COLOR
            ctx.lineWidth = 1.5
            ctx.stroke()
        }
    }

    private drawSelectionHighlights(): void {
        const ctx = this.ctx

        for (const key of this.selectedCells) {
            const parts = key.split(',')
            const r = parseInt(parts[0])
            const c = parseInt(parts[1])
            const cell = this.grid?.[r]?.[c]
            if (!cell?.active) continue

            const { cx, cy } = this.cellCenter(r, c)

            ctx.strokeStyle = SELECTED_COLOR
            ctx.lineWidth = 2
            ctx.beginPath()
            if (cell.kind === 'octagon') {
                this.octagonPath(cx, cy, this.cellSize * 0.44)
            } else {
                this.diamondPath(cx, cy, this.cellSize * 0.30)
            }
            ctx.stroke()

            // Glow effect
            ctx.strokeStyle = 'rgba(0, 255, 136, 0.25)'
            ctx.lineWidth = 4
            ctx.beginPath()
            if (cell.kind === 'octagon') {
                this.octagonPath(cx, cy, this.cellSize * 0.46)
            } else {
                this.diamondPath(cx, cy, this.cellSize * 0.32)
            }
            ctx.stroke()
        }
    }

    // ─── Coordinate Mapping ───────────────────────────────

    /** Map grid (row, col) to canvas center, flipping Y axis to match 3D camera view */
    private cellCenter(r: number, c: number): { cx: number; cy: number } {
        return {
            cx: this.offsetX + (c - this.minC) * this.cellSize + this.cellSize / 2,
            cy: this.offsetY + (this.maxR - r) * this.cellSize + this.cellSize / 2,
        }
    }

    // ─── Path Helpers ────────────────────────────────────

    private octagonPath(cx: number, cy: number, r: number): void {
        for (let i = 0; i < 8; i++) {
            const angle = (Math.PI / 8) + (i * Math.PI / 4)
            const x = cx + r * Math.cos(angle)
            const y = cy + r * Math.sin(angle)
            if (i === 0) this.ctx.moveTo(x, y)
            else this.ctx.lineTo(x, y)
        }
        this.ctx.closePath()
    }

    private diamondPath(cx: number, cy: number, r: number): void {
        this.ctx.moveTo(cx, cy - r)
        this.ctx.lineTo(cx + r, cy)
        this.ctx.lineTo(cx, cy + r)
        this.ctx.lineTo(cx - r, cy)
        this.ctx.closePath()
    }

    // ─── Hit Detection ───────────────────────────────────

    private handleClick(e: MouseEvent): void {
        if (!this.grid || !this.callbacks.onCellClicked) return

        const rect = this.canvas.getBoundingClientRect()
        const mx = e.clientX - rect.left
        const my = e.clientY - rect.top

        // Find closest active octagon within hit radius
        let bestDist = Infinity
        let bestRow = -1
        let bestCol = -1

        for (let r = 0; r < this.grid.length; r++) {
            for (let c = 0; c < (this.grid[0]?.length ?? 0); c++) {
                const cell = this.grid[r]?.[c]
                if (!cell?.active || cell.kind !== 'octagon') continue

                const center = this.cellCenter(r, c)
                const dist = Math.hypot(mx - center.cx, my - center.cy)
                const hitRadius = this.cellSize * 0.45

                if (dist < hitRadius && dist < bestDist) {
                    bestDist = dist
                    bestRow = r
                    bestCol = c
                }
            }
        }

        if (bestRow >= 0) {
            this.callbacks.onCellClicked(bestRow, bestCol)
        }
    }

    // ─── Layout ──────────────────────────────────────────

    private computeBounds(): void {
        const grid = this.grid!
        let minR = Infinity, maxR = -Infinity
        let minC = Infinity, maxC = -Infinity

        for (let r = 0; r < grid.length; r++) {
            for (let c = 0; c < (grid[0]?.length ?? 0); c++) {
                if (grid[r]?.[c]?.active) {
                    minR = Math.min(minR, r)
                    maxR = Math.max(maxR, r)
                    minC = Math.min(minC, c)
                    maxC = Math.max(maxC, c)
                }
            }
        }

        // Add 1 cell padding
        this.minR = Math.max(0, minR - 1)
        this.maxR = Math.min(grid.length - 1, maxR + 1)
        this.minC = Math.max(0, minC - 1)
        this.maxC = Math.min((grid[0]?.length ?? 1) - 1, maxC + 1)
    }
}
